import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { BoardLink } from "../lib/boardLinks";
import ReferralLeaderboard from "../components/ReferralLeaderboard";
import VesselShowroom from "../components/VesselShowroom";
import SovereignMatch from "../components/SovereignMatch";
import SovereignStar from "../components/SovereignStar";
import CommitmentTracker from "../components/CommitmentTracker";
import SolanaPortalSection from "../components/SolanaPortalSection";
import DirectorScout from "../components/DirectorScout";
import LoreInjection from "../components/LoreInjection";
import SovereignSectors from "../components/SovereignSectors";
import FleetDirectory from "../components/FleetDirectory";
import SolanaCheckout from "../components/SolanaCheckout";
import EigooMark from "../components/EigooMark";
import SovereignAnchor from "../components/SovereignAnchor";
import SovereignEye from "../components/SovereignEye";
import HullScanner from "../components/HullScanner";
import HeroStrategyCTA from "../components/HeroStrategyCTA";
import WhatWeSolve from "../components/WhatWeSolve";
import PricingTiers from "../components/PricingTiers";
import BIaaSIntakeForm from "../components/BIaaSIntakeForm";
import FinalCTABanner from "../components/FinalCTABanner";
import { ArrowRight, Flame, Gauge, Landmark, Scroll, Target, Sparkles } from "lucide-react";

export default function Landing() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [usdcOpen, setUsdcOpen] = useState(false);
    const [usdcAmount, setUsdcAmount] = useState(250);
    const [usdcLabel, setUsdcLabel] = useState("EIGOO Sovereign Boarding · Privateer");

    const openUSDC = (tier, price) => {
        setUsdcAmount(price);
        setUsdcLabel(`EIGOO Sovereign Boarding · ${tier}`);
        setUsdcOpen(true);
    };

    return (
        <div className="min-h-screen bg-background grain-overlay">
            {/* Celestial starfield — subtle, decorative, animation drifts every 90s */}
            <div
                aria-hidden="true"
                className="starfield fixed inset-0 pointer-events-none opacity-[0.18] z-0"
            />
            {/* Nav */}
            <header className="relative z-10">
                <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 md:px-10 py-6">
                    <div className="flex items-center gap-3">
                        {/* Logo: gold Sovereign Anchor (wordmark dropped per Director directive 2026.02). */}
                        <EigooMark size="xl" sub="Sovereign Fleet" showWord={false} testid="landing-mark" />
                    </div>
                    <div className="flex items-center gap-3">
                        {user ? (
                            <>
                                <Link
                                    to="/foundry"
                                    data-testid="landing-foundry"
                                    className="hidden md:inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.25em] text-muted-foreground hover:text-[hsl(45_85%_68%)] transition-colors"
                                >
                                    Foundry
                                </Link>
                                <Link
                                    to="/solana"
                                    data-testid="landing-solana"
                                    className="hidden md:inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.25em] text-muted-foreground hover:text-[#FF6FA1] transition-colors"
                                >
                                    Solana
                                </Link>
                                <Link
                                    to="/intel"
                                    data-testid="landing-intel"
                                    className="hidden md:inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.25em] text-muted-foreground hover:text-[hsl(45_85%_68%)] transition-colors"
                                >
                                    Intel Hub
                                </Link>
                                <Link
                                    to="/partners"
                                    data-testid="landing-partners"
                                    className="hidden md:inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.25em] text-muted-foreground hover:text-[#00E5FF] transition-colors"
                                >
                                    Partners
                                </Link>
                                <Link
                                    to="/about"
                                    data-testid="landing-about"
                                    className="hidden md:inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.25em] text-muted-foreground hover:text-[hsl(45_85%_68%)] transition-colors"
                                >
                                    About
                                </Link>
                                <Link
                                    to="/catalog"
                                    data-testid="landing-catalog"
                                    className="hidden md:inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.25em] text-muted-foreground hover:text-[hsl(45_85%_68%)] transition-colors"
                                >
                                    Catalog
                                </Link>
                                <Link
                                    to="/bridge"
                                    data-testid="landing-bridge"
                                    className="hidden md:inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.25em] text-[hsl(45_85%_68%)] hover:text-[hsl(45_85%_85%)] transition-colors"
                                >
                                    Bridge
                                </Link>
                                <button
                                    onClick={() => navigate("/app/map")}
                                    className="px-5 py-2 rounded-full bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] text-sm font-semibold hover:bg-[hsl(45_75%_62%)] transition-colors"
                                    data-testid="landing-enter-portal"
                                >
                                    Enter Portal
                                </button>
                            </>
                        ) : (
                            <>
                                <Link
                                    to="/foundry"
                                    data-testid="landing-foundry-anon"
                                    className="hidden md:inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.25em] text-muted-foreground hover:text-[hsl(45_85%_68%)] transition-colors"
                                >
                                    Foundry
                                </Link>
                                <Link
                                    to="/solana"
                                    data-testid="landing-solana-anon"
                                    className="hidden md:inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.25em] text-muted-foreground hover:text-[#FF6FA1] transition-colors"
                                >
                                    Solana
                                </Link>
                                <Link
                                    to="/intel"
                                    data-testid="landing-intel"
                                    className="hidden md:inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.25em] text-muted-foreground hover:text-[hsl(45_85%_68%)] transition-colors"
                                >
                                    Intel Hub
                                </Link>
                                <Link
                                    to="/partners"
                                    data-testid="landing-partners-anon"
                                    className="hidden md:inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.25em] text-muted-foreground hover:text-[#00E5FF] transition-colors"
                                >
                                    Partners
                                </Link>
                                <Link
                                    to="/about"
                                    data-testid="landing-about-anon"
                                    className="hidden md:inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.25em] text-muted-foreground hover:text-[hsl(45_85%_68%)] transition-colors"
                                >
                                    About
                                </Link>
                                <Link
                                    to="/catalog"
                                    data-testid="landing-catalog-anon"
                                    className="hidden md:inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.25em] text-muted-foreground hover:text-[hsl(45_85%_68%)] transition-colors"
                                >
                                    Catalog
                                </Link>
                                <Link
                                    to="/login"
                                    data-testid="landing-login"
                                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                                >
                                    Sign In
                                </Link>
                                <BoardLink
                                    data-testid="landing-register"
                                    className="px-5 py-2 rounded-full bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] text-sm font-semibold hover:bg-[hsl(45_75%_62%)] transition-colors"
                                >
                                    Board the Fleet
                                </BoardLink>
                            </>
                        )}
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="relative max-w-[1400px] mx-auto px-6 md:px-10 pt-10 md:pt-20 pb-24">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-7 fade-up">
                        <div
                            className="inline-flex items-center gap-2 px-3 py-1 border text-[10px] uppercase tracking-[0.35em] mb-6"
                            style={{
                                borderColor: "#00E5FF",
                                color: "#00E5FF",
                                background: "rgba(0, 229, 255, 0.04)",
                                fontFamily: "'Montserrat', sans-serif",
                                fontWeight: 800,
                            }}
                            data-testid="landing-overline"
                        >
                            <span
                                className="h-1.5 w-1.5 rounded-full animate-pulse"
                                style={{ background: "#00E5FF", boxShadow: "0 0 8px rgba(0,229,255,0.7)" }}
                            />
                            ◢ Silent Partners · Sovereign Network
                        </div>
                        <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter">
                            The command hub for
                            <span className="block gold-gradient-text mt-2">marketing agencies</span>
                            <span className="block text-foreground/90 mt-2 italic font-normal">who run the fleet.</span>
                        </h1>

                        {/* Dontae Kirk · Celestial Proclamation */}
                        <figure
                            className="relative mt-10 mb-2 pl-6 pr-4 py-5 border-l-2 border-[hsl(45_85%_68%)] bg-[hsl(151_55%_5%/0.45)] backdrop-blur rounded-r-md max-w-xl overflow-hidden"
                            data-testid="celestial-quote"
                        >
                            {/* faint starfield wash for the quote pad */}
                            <div
                                aria-hidden="true"
                                className="starfield-pad absolute inset-0 pointer-events-none opacity-60"
                            />
                            <blockquote
                                className="relative text-xl md:text-2xl leading-snug text-[hsl(45_85%_85%)]"
                                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontStyle: "italic", fontWeight: 500 }}
                            >
                                &ldquo;We aren&rsquo;t voyaging in water; we are voyaging through the stars
                                in this infinite cyber space.&rdquo;
                            </blockquote>
                            <figcaption className="relative mt-3 text-[10px] uppercase tracking-[0.4em] text-[hsl(45_85%_68%)]">
                                — Dontae Kirk, Director
                            </figcaption>
                        </figure>

                        <p className="mt-8 text-base md:text-lg text-muted-foreground max-w-xl leading-relaxed">
                            Claim a state. Deploy ad-capital. Track Fuel vs Velocity in real time.
                            Collect your residuals under a transparent 60 / 30 / 10 covenant —
                            every month, in perpetuity.
                        </p>
                        <div className="mt-10 flex flex-wrap gap-4">
                            {user ? (
                                <Link
                                    to="/app/map"
                                    data-testid="hero-cta-primary"
                                    className="group inline-flex items-center gap-2 px-7 py-3.5 font-bold uppercase tracking-[0.22em] text-sm transition-all duration-300"
                                    style={{
                                        fontFamily: "'Montserrat', sans-serif",
                                        background: "#E8C56C",
                                        color: "#050A0F",
                                        border: "1px solid #E8C56C",
                                        boxShadow: "0 0 25px rgba(232,197,108,0.25)",
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.boxShadow = "0 0 35px rgba(232,197,108,0.6)";
                                        e.currentTarget.style.transform = "translateY(-1px)";
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.boxShadow = "0 0 25px rgba(232,197,108,0.25)";
                                        e.currentTarget.style.transform = "translateY(0)";
                                    }}
                                >
                                    [ CLAIM YOUR TERRITORY ]
                                    <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                                </Link>
                            ) : (
                                <BoardLink
                                    data-testid="hero-cta-primary"
                                    className="group inline-flex items-center gap-2 px-7 py-3.5 font-bold uppercase tracking-[0.22em] text-sm transition-all duration-300"
                                    style={{
                                        fontFamily: "'Montserrat', sans-serif",
                                        background: "#E8C56C",
                                        color: "#050A0F",
                                        border: "1px solid #E8C56C",
                                        boxShadow: "0 0 25px rgba(232,197,108,0.25)",
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.boxShadow = "0 0 35px rgba(232,197,108,0.6)";
                                        e.currentTarget.style.transform = "translateY(-1px)";
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.boxShadow = "0 0 25px rgba(232,197,108,0.25)";
                                        e.currentTarget.style.transform = "translateY(0)";
                                    }}
                                >
                                    [ CLAIM YOUR TERRITORY ]
                                    <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                                </BoardLink>
                            )}
                            <Link
                                to="/partners"
                                data-testid="hero-cta-partners"
                                className="inline-flex items-center gap-2 px-7 py-3.5 font-bold uppercase tracking-[0.22em] text-sm transition-all duration-300"
                                style={{
                                    fontFamily: "'Montserrat', sans-serif",
                                    color: "#00E5FF",
                                    border: "1px solid #00E5FF",
                                    background: "transparent",
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = "rgba(0,229,255,0.08)";
                                    e.currentTarget.style.boxShadow = "0 0 20px rgba(0,229,255,0.45)";
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = "transparent";
                                    e.currentTarget.style.boxShadow = "none";
                                }}
                            >
                                [ PARTNERS HUB ]
                            </Link>
                            <Link
                                to="/login"
                                data-testid="hero-cta-secondary"
                                className="inline-flex items-center gap-2 px-5 py-3.5 text-sm transition-colors"
                                style={{
                                    fontFamily: "'Montserrat', sans-serif",
                                    color: "#829AB1",
                                    border: "1px solid rgba(94, 120, 148, 0.4)",
                                }}
                            >
                                Partner Sign In
                            </Link>
                        </div>

                        {/* Strategy Call CTA — Director conversion layer */}
                        <div className="mt-6">
                            <HeroStrategyCTA />
                        </div>

                        {/* Sovereign Boarding tiers — Director directive 2026.02
                            ($250 Privateer · $1,000 Admiral · $5,000 Sovereign) */}
                        <div
                            data-testid="tier-cards-usdc"
                            className="mt-16 grid grid-cols-3 gap-3 max-w-xl"
                        >
                            {[
                                { tier: "Privateer", price: 250, sublabel: "First Light Rate", color: "from-[hsl(40_60%_45%)] to-[hsl(45_85%_65%)]" },
                                { tier: "Admiral", price: 1000, sublabel: "Boarding Fee", color: "from-[hsl(45_85%_55%)] to-[hsl(50_90%_70%)]" },
                                { tier: "Sovereign", price: 5000, sublabel: "Elite Commission", color: "from-[hsl(40_75%_50%)] to-[hsl(45_95%_75%)]" },
                            ].map((t) => {
                                const usdcPrice = Math.round(t.price * 0.95);
                                return (
                                    <button
                                        key={t.tier}
                                        type="button"
                                        onClick={() => openUSDC(t.tier, t.price)}
                                        data-testid={`tier-card-${t.tier.toLowerCase()}`}
                                        className="relative group glass rounded-xl p-4 text-left hover:border-[hsl(45_85%_68%/0.6)] transition-colors"
                                    >
                                        <div className={`inline-block text-[10px] uppercase tracking-[0.25em] bg-gradient-to-r ${t.color} bg-clip-text text-transparent font-bold`}>
                                            {t.tier}
                                        </div>
                                        <div className="font-heading text-2xl mt-1 text-foreground">${t.price.toLocaleString()}</div>
                                        <div className="text-[10px] text-muted-foreground mt-1">{t.sublabel}</div>

                                        <div
                                            className="mt-3 flex items-center gap-1.5 text-[9px] uppercase tracking-[0.3em] font-mono"
                                            style={{ color: "#14F195" }}
                                        >
                                            <Sparkles size={9} /> USDC ${usdcPrice.toLocaleString()} · save 5%
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Right panel · Sovereign Covenant 60/30/10 — Director directive 2026.02
                        (Celestial Starfield background + gold Anchor lockup) */}
                    <div className="lg:col-span-5 fade-up" style={{ animationDelay: "0.15s" }} data-testid="covenant-panel">
                        <div className="glass rounded-2xl p-8 relative overflow-hidden">
                            {/* Starfield wash — drives the "voyaging through the stars" aesthetic */}
                            <div
                                aria-hidden="true"
                                className="starfield absolute inset-0 pointer-events-none opacity-[0.35]"
                            />
                            <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full bg-[hsl(40_60%_45%/0.18)] blur-3xl" />
                            <div className="relative">
                                <div className="flex items-center gap-3">
                                    {/* Gold Sovereign Anchor — replaces the EIGOO wordmark per Director directive */}
                                    <SovereignAnchor
                                        size={42}
                                        strokeWidth={6}
                                        className="text-[hsl(45_85%_68%)] drop-shadow-[0_2px_8px_hsl(40_70%_45%/0.45)]"
                                    />
                                    <div>
                                        <div className="text-[10px] uppercase tracking-[0.35em] text-muted-foreground">Sovereign Covenant</div>
                                        <div className="text-[10px] uppercase tracking-[0.3em] text-[hsl(45_85%_68%)]">EIGOO INC</div>
                                    </div>
                                </div>
                                <div
                                    className="font-heading text-5xl mt-5 gold-gradient-text"
                                    data-testid="covenant-split-display"
                                >
                                    60 / 30 / 10
                                </div>
                                <div className="text-sm text-muted-foreground mt-2">
                                    Commander / Director / Anchor share on yield.
                                </div>

                                <div className="mt-8 space-y-4">
                                    {[
                                        { icon: Landmark, label: "50 States", sub: "Privateer → Sovereign tiers" },
                                        { icon: Flame, label: "Fuel Tracking", sub: "Ad Spend, per campaign" },
                                        { icon: Gauge, label: "Velocity", sub: "Net Revenue, live" },
                                        { icon: Scroll, label: "Scout Reports", sub: "Intel, optimisation, pivots" },
                                        { icon: Target, label: "Ad Kits", sub: "Localised hooks + targeting" },
                                    ].map((item) => (
                                        <div key={item.label} className="flex items-center gap-3">
                                            <div className="w-9 h-9 rounded-md bg-[hsl(40_61%_20%)] flex items-center justify-center text-[hsl(45_85%_68%)]">
                                                <item.icon size={16} strokeWidth={1.5} />
                                            </div>
                                            <div>
                                                <div className="text-sm font-medium">{item.label}</div>
                                                <div className="text-xs text-muted-foreground">{item.sub}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Sovereign Eye · master visual for Ad Data (Director directive 2026.02) */}
            <section className="relative max-w-[1400px] mx-auto px-6 md:px-10 py-16">
                <div className="max-w-3xl mb-8 fade-up">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[hsl(40_50%_40%/0.4)] text-[10px] uppercase tracking-[0.3em] text-[hsl(45_60%_70%)] mb-4">
                        <span className="w-1.5 h-1.5 rounded-full bg-[hsl(350_75%_60%)] animate-pulse" />
                        Live Threat Field · Public Channel
                    </div>
                    <h2 className="font-heading text-4xl sm:text-5xl tracking-tighter leading-[1.05]">
                        The world is loud.
                        <span className="block gold-gradient-text mt-2">The Fleet sees through the noise.</span>
                    </h2>
                    <p className="mt-5 text-base text-muted-foreground max-w-xl leading-relaxed">
                        Anchored at Honolulu. Real-time global cyber-threat noise rendered as red arcs.
                        Vetted partners see the gold layer underneath — claimed territories, B2B leads,
                        active deliveries.
                    </p>
                </div>
                <SovereignEye mode="public" height={560} />
            </section>

            {/* Hull Scanner · primary B2B conversion (Director directive 2026.02) */}
            <section className="container mx-auto max-w-6xl px-4 sm:px-6 mt-16 sm:mt-24">
                <HullScanner />
            </section>

            {/* EIGOO Lore Injection — acronym doctrine */}
            <LoreInjection />

            {/* Sovereign Sectors — 5 monetization tracks (Universal Sync) */}
            <SovereignSectors />

            {/* Master Directory — Strike #25 — Every hull, one bridge. */}
            <FleetDirectory />

            {/* Director's Scout — I-Want Engine, primary engagement CTA */}
            <DirectorScout />

            {/* Sovereign Star — primary engagement engine, mounted right under the hero */}
            <SovereignStar />

            {/* Commitment Tracker — Skin in the Game leaderboard immediately after the feed */}
            <CommitmentTracker />

            {/* Vessel Showroom */}
            <VesselShowroom />

            {/* Solana Portal — DSRE Charter teaser */}
            <SolanaPortalSection />

            {/* Sovereign Match — Tech Ops vs Vault scouter */}
            <SovereignMatch />

            {/* Leaderboard */}
            <ReferralLeaderboard />

            {/* ===== BIaaS Conversion Layer (Director directive 2026.07) ===== */}
            <WhatWeSolve />
            <PricingTiers />
            <BIaaSIntakeForm />
            <FinalCTABanner />

            {/* USDC One-Click Checkout modal */}
            <SolanaCheckout
                open={usdcOpen}
                onClose={() => setUsdcOpen(false)}
                amount={usdcAmount}
                label={usdcLabel}
                message="Sovereign boarding · USDC"
                sector="finance"
                vessel="hub"
                purpose={usdcLabel}
            />

            {/* Footer */}
            <footer className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 border-t border-[hsl(40_40%_30%/0.15)] text-xs text-muted-foreground flex flex-wrap items-center justify-between gap-4">
                <div>© EIGOO Fleet · All residuals in perpetuity.</div>
                <div className="font-mono">{`[state].[sector].eigooinc.net`}</div>
            </footer>
        </div>
    );
}
